import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Seo } from "../lib/seo";
import { formatDate } from "../lib/format";
import { useLeadDialog } from "../context/LeadDialogContext";
import { useLang } from "../context/LanguageContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { track } from "../lib/tracking";
import { Loader2, ChevronRight, MessageCircle, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import "../styles/article.css";

const WHATSAPP = "393456612447";
const ORG = "Advanced Post Graduate Excellence";

export default function ArticlePage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [a, setA] = useState(null);
  const [status, setStatus] = useState("loading");
  const [related, setRelated] = useState([]);
  const lead = useLeadDialog();
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    setStatus("loading");
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`)
      .then(async (r) => {
        setA(r.data);
        setStatus("ok");
        try {
          const list = (await api.get("/articles", { params: { limit: 100 } })).data;
          const ids = r.data.related_ids || [];
          let rel = list.filter((x) => ids.includes(x.id));
          if (rel.length === 0) rel = list.filter((x) => x.slug !== r.data.slug).slice(0, 3);
          setRelated(rel.slice(0, 4));
        } catch { /* ignore */ }
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" /></div>;
  }
  if (status === "notfound") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-heading text-5xl font-light text-[#0B1220]">Articolo non trovato</h1>
        <Link to="/blog" className="mt-8 bg-[#1452F0] text-[#F4F8FF] rounded-sm px-7 py-3.5 text-sm font-medium">Torna al blog</Link>
      </div>
    );
  }

  const url = `${origin}/blog/${a.slug}`;
  const img = a.featured_image ? mediaUrl(a.featured_image) : "";
  const keywords = [a.keyword_primary, ...(a.keywords_secondary || [])].filter(Boolean).join(", ");
  const faq = (a.faq || []).filter((f) => f.question && f.answer);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: a.h1,
      description: a.meta_description || a.subtitle || "",
      image: img ? [img] : [],
      datePublished: a.published_at || a.created_at,
      dateModified: a.updated_at,
      author: { "@type": "Organization", name: a.author || ORG },
      publisher: { "@type": "Organization", name: ORG, url: origin },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      ...(keywords ? { keywords } : {}),
      ...(a.category ? { articleSection: a.category } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
        { "@type": "ListItem", position: 3, name: a.h1, item: url },
      ],
    },
    ...(faq.length ? [{
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
    }] : []),
  ];

  const waText = encodeURIComponent(`Ciao, vorrei informazioni sul corso: ${a.h1}`);

  return (
    <div className="bg-[#F4F8FF]">
      <Seo
        title={a.meta_title || a.h1}
        description={a.meta_description || a.subtitle}
        canonical={a.canonical_url || url}
        image={img}
        type="article"
        jsonLd={jsonLd}
      />

      <main className="px-6 md:px-12 max-w-[820px] mx-auto py-12 md:py-16">
        <nav aria-label="breadcrumb" data-testid="article-breadcrumb" className="flex items-center gap-1.5 text-sm text-[#5C6678] mb-8 flex-wrap">
          <Link to="/" className="hover:text-[#1452F0]">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/blog" className="hover:text-[#1452F0]">Blog</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-[#0B1220]">{a.h1}</span>
        </nav>

        <article>
          <header>
            {a.category && <p className="overline text-[#1452F0] mb-2">{a.category}</p>}
            <h1 data-testid="article-h1" className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B1220] leading-[1.08]">
              {a.h1}
            </h1>
            {a.subtitle && <p className="mt-4 text-xl text-[#5C6678] font-light">{a.subtitle}</p>}
            <p className="mt-4 text-sm text-[#8A94A6]">
              Di {a.author || ORG} · {formatDate(a.published_at || a.created_at, lang)}
            </p>
          </header>

          {a.featured_image && (
            <img src={mediaUrl(a.featured_image)} alt={a.featured_image_alt || a.h1} className="w-full h-auto rounded-lg mt-8" />
          )}

          {(a.summary_points || []).filter(Boolean).length > 0 && (
            <section aria-label="In sintesi" data-testid="article-summary" className="mt-10 bg-[#E9EFFA] border-l-4 border-[#1452F0] rounded-sm p-6">
              <h2 className="font-heading text-lg text-[#0B1220] mb-3">In sintesi</h2>
              <ul className="space-y-2">
                {a.summary_points.filter(Boolean).map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[#0B1220]/85">
                    <Check className="h-4 w-4 text-[#1452F0] mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div
            className="article-content mt-10"
            data-testid="article-body"
            dangerouslySetInnerHTML={{ __html: a.body_html || "" }}
          />

          {faq.length > 0 && (
            <section aria-label="Domande frequenti" data-testid="article-faq" className="mt-14">
              <h2 className="font-heading text-3xl font-light text-[#0B1220] mb-6">Domande frequenti</h2>
              <Accordion type="single" collapsible className="w-full">
                {faq.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-[#D5DEEE]">
                    <AccordionTrigger className="text-left text-[#0B1220] hover:text-[#1452F0]">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-[#5C6678] leading-relaxed">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {/* CTA */}
          <section data-testid="article-cta" className="mt-14 bg-[#0B1220] rounded-lg p-8 md:p-10 relative isolate overflow-hidden">
            <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-[#1452F0]/30 blur-3xl pointer-events-none" />
            <h2 className="font-heading text-2xl md:text-3xl font-light text-white relative">Interessato a questo corso?</h2>
            <p className="mt-2 text-[#9AA7BD] font-light relative">Richiedi informazioni o iscriviti: posti a numero limitato.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 relative">
              {a.cta_course_slug ? (
                <Link
                  to={`/corsi/${a.cta_course_slug}`}
                  data-testid="article-enroll-btn"
                  className="bg-[#1452F0] text-white hover:bg-[#0E3DC0] transition-colors rounded-sm px-6 py-3.5 text-sm font-medium flex items-center justify-center gap-2 shadow-[0_0_28px_rgba(20,82,240,0.45)]"
                >
                  Iscriviti al corso <ArrowUpRight className="h-4 w-4" />
                </Link>
              ) : null}
              <button
                data-testid="article-info-btn"
                onClick={() => lead?.openLead({ title: a.h1 }, "article")}
                className="border border-white/30 text-white hover:bg-white hover:text-[#0B1220] transition-colors rounded-sm px-6 py-3.5 text-sm font-medium flex items-center justify-center gap-2"
              >
                Richiedi informazioni <ArrowRight className="h-4 w-4" />
              </button>
              {a.show_whatsapp !== false && (
                <a
                  href={`https://wa.me/${WHATSAPP}?text=${waText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="article-whatsapp-btn"
                  onClick={() => track("click_whatsapp", { article: a.h1 })}
                  className="bg-[#25D366] text-white hover:bg-[#1da851] transition-colors rounded-sm px-6 py-3.5 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              )}
            </div>
          </section>

          {related.length > 0 && (
            <aside aria-label="Articoli correlati" data-testid="article-related" className="mt-14">
              <h2 className="font-heading text-2xl font-light text-[#0B1220] mb-5">Articoli correlati</h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <li key={r.id}>
                    <Link to={`/blog/${r.slug}`} className="block bg-[#E9EFFA] rounded-sm p-5 hover:bg-[#dce6f7] transition-colors">
                      <span className="font-medium text-[#0B1220]">{r.h1}</span>
                      {r.subtitle && <span className="block text-sm text-[#5C6678] mt-1 line-clamp-2">{r.subtitle}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          {(a.tags || []).filter(Boolean).length > 0 && (
            <footer className="mt-12 pt-6 border-t border-[#D5DEEE] flex flex-wrap gap-2">
              {a.tags.filter(Boolean).map((t) => (
                <span key={t} className="text-xs bg-[#E9EFFA] text-[#5C6678] px-3 py-1.5 rounded-full">#{t}</span>
              ))}
            </footer>
          )}
        </article>
      </main>
    </div>
  );
}
